/* eslint-disable react/prop-types */
import * as React from 'react';

import { useLockFn, useUpdateEffect } from 'ahooks';
import {
  Badge,
  Button,
  Dropdown,
  Flex,
  Modal,
  notification,
  Space,
} from 'antd';

import {
  AntdRouterExternalLink,
  AntdRouterLink,
  DeleteOutlinedIcon,
  EditOutlinedIcon,
  MoreOutlinedIcon,
  PrimitiveTable,
} from 'components';
import { deletePackagingModel } from 'services';
import type { PackagingModel } from 'types';
import { adjustPageNumber, generateTableDataSource, PATHNAME } from 'utils';

const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    CATEGORIES: {
      BASE_URL: CATEGORIES_BASE_URL,
      UPDATE_URL: CATEGORIES_UPDATE_URL,
    },
    PACKAGING_MODELS: { UPDATE_URL: PACKAGING_MODELS_UPDATE_URL },
  },
} = PATHNAME;

type PackagingModelsTableProps = {
  loading: boolean;
  total: number;
  packagingModels: PackagingModel[];
  pageSize: number;
  pageNumber: number;
  handlePageSize: (pageSize: number) => void;
  handlePageNumber: (pageNumber: number) => void;
  handleRefreshData: () => void;
};

const PackagingModelsTable = React.memo<PackagingModelsTableProps>(
  ({
    loading,
    total,
    packagingModels,
    pageSize,
    pageNumber,
    handlePageSize,
    handlePageNumber,
    handleRefreshData,
  }) => {
    const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();

    useUpdateEffect(() => {
      adjustPageNumber(total, pageSize, pageNumber, handlePageNumber);
    }, [total, pageSize, pageNumber]);

    const handleDeletePackagingModel = useLockFn(
      async ({ id }: PackagingModel) => {
        deletePackagingModel({ id })
          .then(() => {
            handleRefreshData();
            notification.success({
              message: 'بسته‌بندی مورد نظر با موفقیت حذف شد.',
            });
          })
          .catch(message => {
            notification.error({
              message,
            });
          });
      },
    );

    const packagingModelsDataSource = generateTableDataSource(
      packagingModels,
      'id',
    );

    return (
      <PrimitiveTable
        columns={[
          {
            title: 'نام',
            dataIndex: 'name',
            key: 'name',
            width: '30%',
            ellipsis: true,
            render: (_, { id, name }) => (
              <AntdRouterLink
                ellipsis
                to={`${PACKAGING_MODELS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
              >
                {name}
              </AntdRouterLink>
            ),
          },
          {
            title: 'دسته‌بندی‌ها',
            dataIndex: 'categories',
            key: 'categories',
            ellipsis: true,
            render: (_, { categories }) => (
              <Space wrap>
                {categories.map(({ id, title }, index) => {
                  const isInitialItem = index === 0;
                  return (
                    <React.Fragment key={id}>
                      {!isInitialItem ? (
                        <Badge status='default' className='mx-0.5' />
                      ) : null}
                      <AntdRouterExternalLink
                        ellipsis
                        href={`${CATEGORIES_BASE_URL}${CATEGORIES_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                      >
                        {title}
                      </AntdRouterExternalLink>
                    </React.Fragment>
                  );
                })}
              </Space>
            ),
          },
          {
            title: 'برچسب',
            dataIndex: 'label',
            key: 'label',
            width: 100,
            ellipsis: true,
            render: (_, { label }) => label,
          },
          {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 75,
            fixed: 'right',
            render: (_, record) => {
              const { id } = record;
              return (
                <Flex align='center' justify='center'>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'update-packaging-model',
                          label: (
                            <AntdRouterLink
                              to={`${PACKAGING_MODELS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                              className='inline-block py-1 text-inherit'
                            >
                              <EditOutlinedIcon />
                              <span className='ms-2 text-sm'>
                                ویرایش بسته‌بندی
                              </span>
                            </AntdRouterLink>
                          ),
                        },
                        {
                          key: 'delete-packaging-model',
                          label: (
                            <React.Fragment>
                              <Button
                                danger
                                type='link'
                                size='small'
                                className='my-0.5 flex items-center px-0'
                                onClick={async () => {
                                  const confirmed =
                                    await deleteModalInstance.confirm({
                                      title:
                                        'آیا از حذف بسته‌بندی مورد نظر اطمینان دارید؟',
                                      width: 500,
                                    });
                                  if (confirmed) {
                                    handleDeletePackagingModel(record);
                                  }
                                }}
                              >
                                <DeleteOutlinedIcon />
                                <span className='text-sm'>حذف بسته‌بندی</span>
                              </Button>
                              {deleteModalContextHolder}
                            </React.Fragment>
                          ),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button size='small' className='pb-[18px] pt-3.5'>
                      <MoreOutlinedIcon className='text-xl' />
                      <span className='sr-only'>آیکون اکشن‌ها</span>
                    </Button>
                  </Dropdown>
                </Flex>
              );
            },
          },
        ]}
        loading={loading}
        dataSource={packagingModelsDataSource}
        pagination={{
          total,
          pageSize,
          current: pageNumber,
          showTotal: () => `در مجموع ${total} مورد`,
          onChange: (page, size) => {
            handlePageSize(size);
            if (page !== pageNumber) {
              handlePageNumber(page);
            }
          },
        }}
      />
    );
  },
);
PackagingModelsTable.displayName = 'PackagingModelsTable';

export { PackagingModelsTable };
